import {
  Box,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { useObjectDefinitionContext } from "../ObjectDefinitionContext";
import DisplayFieldsSection from "./DisplayFIeldsSection";
import type {
  ObjectDefinitionGeneralForm,
  ObjectDefinitionGeneralFormValidation,
} from "./objectDefinitionGeneralState";
import ObjectIdentifierFieldsSection from "./ObjectIdentifierFieldsSection";
import ObjectProductsTable from "./ObjectProductsTable";
import ObjectSupportedFeaturesSection from "./ObjectSupportedFeaturesSection";

interface SectionHeaderProps {
  title: string;
  subtitle?: string;
}

const SectionHeader = ({ title, subtitle }: SectionHeaderProps) => (
  <Box sx={{ width: "100%", py: 2 }}>
    <Typography variant="subtitle1">{title}</Typography>
    {subtitle && <Typography color="textSecondary">{subtitle}</Typography>}
  </Box>
);

const SectionSeparator = () => <Divider sx={{ my: 3 }} />;

interface Props {
  form: ObjectDefinitionGeneralForm;
  onFormChange: (form: ObjectDefinitionGeneralForm) => void;
  validationResult: ObjectDefinitionGeneralFormValidation;
}

const ObjectDetailsGeneral = ({ form, onFormChange, validationResult }: Props) => {
  const { objectDefinition, hasEditPermissions } = useObjectDefinitionContext();

  return (
    <Container maxWidth="md" disableGutters sx={{ height: "100%" }}>
      <ScrollableFlexContainer sx={{ height: "100%" }}>
        <Stack spacing={2.5}>
          <SectionHeader title="General" />
          <TextField
            disabled={!hasEditPermissions}
            variant="outlined"
            label="Name"
            value={form.name}
            onChange={(e) => onFormChange({ ...form, name: e.target.value })}
            error={Boolean(validationResult.nameResult.error)}
            helperText={validationResult.nameResult.error}
          />
          <Stack spacing={2}>
            <FormControlLabel
              disabled
              control={<Checkbox disabled checked={Boolean(objectDefinition.businessCentralType)} />}
              label={<Typography>Linked to a Fund Operations table</Typography>}
            />
            {objectDefinition.businessCentralType && (
              <TextField
                disabled
                select
                variant="outlined"
                label="Table Name"
                value={objectDefinition.businessCentralType}
              >
                <MenuItem value={objectDefinition.businessCentralType}>{objectDefinition.businessCentralType}</MenuItem>
              </TextField>
            )}
          </Stack>
          <Stack>
            <SectionHeader title="Product" subtitle="Products associated with this object type." />
            <ObjectProductsTable products={objectDefinition.productAreas} />
          </Stack>
        </Stack>

        <SectionSeparator />

        <SectionHeader
          title="Supported Features"
          subtitle="Customize the functionalities available for this object type."
        />
        <ObjectSupportedFeaturesSection
          features={form.supportedFeatures}
          onChange={(supportedFeatures) => onFormChange({ ...form, supportedFeatures })}
          checklists={objectDefinition.checklists}
        />

        <SectionSeparator />

        <SectionHeader
          title="Unique Identifier"
          subtitle="Current unique identifier for this object type. The unique identifier ensures that each record of the object type can be distinctly recognized."
        />
        <ObjectIdentifierFieldsSection allFields={objectDefinition.fields} />

        <SectionSeparator />

        <SectionHeader
          title="Display Fields"
          subtitle="Fields used to display the object in the system. The primary is used to name each object record, while the secondary adds more context."
        />
        <DisplayFieldsSection
          primaryDisplayFieldId={objectDefinition.displayFields.primaryFieldId}
          secondaryDisplayFieldId={objectDefinition.displayFields.secondaryFieldId}
          allFields={objectDefinition.fields}
        />
      </ScrollableFlexContainer>
    </Container>
  );
};

export default ObjectDetailsGeneral;
